<template>
  <div class="indicator-selector">
    <!-- Top Search Bar -->
    <div class="is-search-container">
      <div class="is-search-wrapper">
        <svg class="is-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input 
          v-model="searchQuery" 
          class="is-search-input" 
          type="text"
          placeholder="Search indicators..."
          aria-label="Search indicators"
          ref="searchInput"
        />
        <button 
          v-if="searchQuery" 
          class="is-search-clear"
          aria-label="Clear indicator search"
          @click="searchQuery = ''"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Main Content: Sidebar + List -->
    <div class="is-main-content">
      <!-- Left Sidebar -->
      <div class="is-sidebar">
        <!-- Personal Section -->
        <div class="is-sidebar-section">
          <span class="is-sidebar-section-label">PERSONAL</span>
          <button 
            class="is-sidebar-item"
            :class="{ active: activeSection === 'my-scripts' }"
            @click="setSection('my-scripts')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span>My Scripts</span>
          </button>
        </div>

        <!-- Built-in Section -->
        <div class="is-sidebar-section">
          <span class="is-sidebar-section-label">BUILT-IN</span>
          <button 
            class="is-sidebar-item"
            :class="{ active: activeSection === 'system' && !activeSubCategory }"
            @click="setSection('system')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/>
            </svg>
            <span>All Indicators</span>
          </button>
          <button 
            v-for="cat in systemCategories" 
            :key="cat"
            :title="cat"
            class="is-sidebar-item is-sidebar-subitem"
            :class="{ active: activeSection === 'system' && activeSubCategory === cat }"
            @click="setSubCategory(cat)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            <span>{{ cat }}</span><span class="is-category-count">{{ categoryCounts[cat] }}</span>
          </button>
        </div>

        <!-- Groups Section -->
        <div class="is-sidebar-section">
          <span class="is-sidebar-section-label">GROUPS</span>
          <button 
            class="is-sidebar-item"
            :class="{ active: activeSection === 'groups' }"
            @click="setSection('groups')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            <span>My Groups</span>
            <span v-if="groupCount > 0" class="is-sidebar-badge">{{ groupCount }}</span>
          </button>
        </div>
      </div>

      <!-- Right Content Panel -->
      <div class="is-content-panel">
        <!-- Groups View -->
        <div v-if="activeSection === 'groups'" class="is-groups-view">
          <GroupManager
            ref="groupManager"
            :current-indicators="selectedIndicators"
            :available-indicators="groupAvailableIndicators"
            @apply-group="onApplyGroup"
            @group-selected="onGroupSelected"
            @groups-loaded="onGroupsLoaded"
          />
        </div>

        <!-- Indicators List View -->
        <div v-else class="is-list-view">
          <!-- Sub-header showing context -->
          <div class="is-list-header">
            <span class="is-list-title">{{ listTitle }}</span>
            <span class="is-list-count" role="status" aria-live="polite">{{ filteredIndicators.length }} indicators</span>
          </div>

          <!-- Indicators List -->
          <div class="is-indicators-list">
            <div v-if="filteredIndicators.length === 0" class="is-empty-state">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <p>No indicators found</p>
              <span v-if="activeSection === 'my-scripts'">Create custom scripts using the Script Editor</span>
              <span v-else>Try adjusting your search or category</span>
            </div>
            
            <div 
              v-for="indicator in filteredIndicators" 
              :key="indicator.name"
              class="is-indicator-item"
              role="checkbox"
              tabindex="0"
              :aria-label="'Select '+indicator.name"
              :aria-checked="isIndicatorSelected(indicator.name) ? 'true' : 'false'"
              @keydown.space.prevent="toggleIndicator(indicator)"
              @keydown.enter.prevent="toggleIndicator(indicator)"
              :class="{ selected: isIndicatorSelected(indicator.name) }"
              @click="toggleIndicator(indicator)"
            >
              <div class="is-indicator-left">
                <div class="is-checkbox" :class="{ checked: isIndicatorSelected(indicator.name) }">
                  <svg v-if="isIndicatorSelected(indicator.name)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div class="is-indicator-info">
                  <span class="is-indicator-name">{{ indicator.name }}</span>
                  <span class="is-indicator-desc">{{ getIndicatorDescription(indicator) }}</span>
                </div>
              </div>
              <span v-if="indicator._isCustom" class="is-custom-badge">Custom</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Action Bar (visible when indicators are selected) -->
    <div class="is-action-bar" v-if="selectedIndicators.length > 0 && activeSection !== 'groups'">
      <div class="is-selected-summary">
        <span class="is-selected-count">{{ selectedIndicators.length }} selected</span>
        <div class="is-selected-tags">
          <span 
            v-for="(indicator, index) in selectedIndicators.slice(0, 4)" 
            :key="indicator.name"
            class="is-selected-tag"
          >
            {{ indicator.name }}
            <button class="is-tag-remove" @click.stop="removeIndicator(index)">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </span>
          <span v-if="selectedIndicators.length > 4" class="is-selected-tag is-tag-more">
            +{{ selectedIndicators.length - 4 }}
          </span>
        </div>
      </div>
      <div class="is-action-buttons">
        <button 
          class="is-apply-btn"
          @click="applyToChart"
        >
          Apply to Chart
        </button>
        <button 
          class="is-group-btn"
          @click="addToGroup"
          title="Save selected indicators as a group"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            <line x1="12" y1="11" x2="12" y2="17"/>
            <line x1="9" y1="14" x2="15" y2="14"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GroupManager from './GroupManager.vue'
import { loadAllIndicators } from '../candle-script/storage.js'

import {studyCategory,categoryCounts} from '../workspace/study-categories.mjs'
export default {
  name: 'IndicatorSelector',
  components: { GroupManager },
  props: {
    indicators: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'selection-change', 'apply-to-chart', 'add-to-group', 'apply-group'],
  data() {
    return {
      searchQuery: '',
      activeSection: 'system',
      activeSubCategory: null,
      selectedIndicators: [...this.modelValue],
      customScripts: [],
      groupCount: 0
    }
  },
  computed: {
    categoryCounts(){return categoryCounts(this.indicators)},
    systemCategories() {
      return Object.keys(this.categoryCounts).sort((a,b)=>a.localeCompare(b))
    },
    listTitle() {
      if (this.activeSection === 'my-scripts') return 'My Scripts'
      if (this.activeSection === 'system') {
        return this.activeSubCategory || 'All Indicators'
      }
      return 'Indicators'
    },
    filteredIndicators() {
      let filtered = []

      if (this.activeSection === 'my-scripts') {
        // Show custom/user scripts
        filtered = this.customScripts.map(script => ({
          name: script.name || script.id,
          type: script.name || script.id,
          settings: {},
          _isCustom: true,
          _script: script
        }))
      } else if (this.activeSection === 'system') {
        filtered = this.indicators
        if (this.activeSubCategory) {
          filtered = filtered.filter(indicator=>studyCategory(indicator.type||indicator.name)===this.activeSubCategory)
        }
      }

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(indicator => {
          const name = (indicator.name || '').toLowerCase()
          const description = this.getIndicatorDescription(indicator).toLowerCase()
          const text=name+' '+description+' '+studyCategory(indicator.type||indicator.name).toLowerCase()
          return query.trim().split(/\s+/).every(term=>text.includes(term))
        })
      }

      return filtered
    },
    groupAvailableIndicators() {
      const system = this.indicators.map(indicator => ({
        name: indicator.name,
        type: indicator.type || indicator.name,
        settings: indicator.settings || {},
        side: this.getIndicatorSide(indicator),
        description: this.getIndicatorDescription(indicator)
      }))
      const custom = this.customScripts.map(script => ({
        name: script.name || script.id,
        type: script.name || script.id,
        settings: {},
        description: script.description || 'Custom script',
        _isCustom: true
      }))
      return [...system, ...custom]
    }
  },
  mounted() {
    this.loadCustomScripts()
    this.$nextTick(() => {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus()
      }
    })
  },
  methods: {
    loadCustomScripts() {
      try {
        this.customScripts = loadAllIndicators() || []
      } catch (e) {
        this.customScripts = []
      }
    },
    setSection(section) {
      this.activeSection = section
      this.activeSubCategory = null
      if (section === 'my-scripts') {
        this.loadCustomScripts()
      }
      if (section === 'groups') {
        this.$nextTick(() => {
          const gm = this.$refs.groupManager
          if (gm) gm.loadGroups()
        })
      }
    },
    setSubCategory(cat) {
      this.activeSection = 'system'
      this.activeSubCategory = cat
    },
    getIndicatorDescription(indicator) {
      try {
        if (indicator._isCustom && indicator._script) {
          return indicator._script.description || 'Custom script'
        }
        return indicator.methods?.meta_info()?.desc || ''
      } catch (e) {
        return ''
      }
    },
    getIndicatorSide(indicator) {
      try {
        return indicator.side || indicator.methods?.meta_info()?.preset?.side || 'onchart'
      } catch (e) {
        return indicator.side || 'onchart'
      }
    },
    isIndicatorSelected(name) {
      return this.selectedIndicators.some(ind => ind.name === name)
    },
    toggleIndicator(indicator) {
      const index = this.selectedIndicators.findIndex(ind => ind.name === indicator.name)
      if (index >= 0) {
        this.selectedIndicators.splice(index, 1)
      } else {
        this.selectedIndicators.push({
          name: indicator.name,
          type: indicator.type || indicator.name,
          side: this.getIndicatorSide(indicator),
          settings: indicator.settings || {},
          _isCustom: indicator._isCustom || false
        })
      }
      this.emitChange()
    },
    removeIndicator(index) {
      this.selectedIndicators.splice(index, 1)
      this.emitChange()
    },
    emitChange() {
      this.$emit('update:modelValue', this.selectedIndicators)
      this.$emit('selection-change', this.selectedIndicators)
    },
    applyToChart() {
      this.$emit('apply-to-chart', this.selectedIndicators)
    },
    addToGroup() {
      this.activeSection = 'groups'
      this.$nextTick(() => {
        const gm = this.$refs.groupManager
        if (gm) gm.startCreateGroup()
      })
      this.$emit('add-to-group', this.selectedIndicators)
    },
    onApplyGroup(group) {
      this.$emit('apply-group', group)
    },
    onGroupSelected(group) {
      // Could pre-select group indicators
    },
    onGroupsLoaded(count) {
      this.groupCount = count
    }
  },
  watch: {
    modelValue: {
      handler(newValue) {
        this.selectedIndicators = [...newValue]
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.indicator-selector {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ── Search ── */
.is-search-container {
  padding: 12px 16px 8px;
  flex-shrink: 0;
}

.is-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.is-search-icon {
  position: absolute;
  left: 12px;
  color: var(--tc-text-tertiary);
  pointer-events: none;
}

.is-search-input {
  width: 100%;
  padding: 9px 36px 9px 38px;
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  font-size: 13px;
  background: var(--tc-input-bg);
  color: var(--tc-input-text);
  outline: none;
  transition: border-color 0.15s ease;
}

.is-search-input:focus {
  border-color: var(--tc-accent);
}

.is-search-input::placeholder {
  color: var(--tc-input-placeholder);
}

.is-search-clear {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--tc-text-tertiary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-search-clear:hover {
  color: var(--tc-text-primary);
}

/* ── Main Content Layout ── */
.is-main-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ── Sidebar ── */
.is-sidebar {
  width: 180px;
  min-width: 180px;
  border-right: 1px solid var(--tc-border-primary);
  overflow-y: auto;
  padding: 8px 0;
  flex-shrink: 0;
}

.is-sidebar::-webkit-scrollbar {
  width: 4px;
}

.is-sidebar::-webkit-scrollbar-thumb {
  background: var(--tc-scrollbar-thumb);
  border-radius: 2px;
}

.is-sidebar-section {
  margin-bottom: 12px;
}

.is-sidebar-section-label {
  display: block;
  padding: 4px 16px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--tc-text-tertiary);
  text-transform: uppercase;
}

.is-sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  color: var(--tc-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s ease;
  border-radius: 0;
}

.is-sidebar-item:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
}

.is-sidebar-item.active {
  background: var(--tc-bg-active, var(--tc-bg-hover));
  color: var(--tc-text-primary);
  font-weight: 600;
}

.is-sidebar-subitem {
  padding-left: 28px;
  font-size: 12px;
  font-weight: 400;
}

.is-sidebar-subitem svg {
  opacity: 0.5;
}

.is-sidebar-badge {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--tc-accent);
  color: #ffffff;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
}

/* ── Content Panel ── */
.is-content-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Groups View ── */
.is-groups-view {
  flex: 1;
  overflow: hidden;
}

.is-groups-view .group-manager {
  height: 100%;
}

/* ── List View ── */
.is-list-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.is-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--tc-border-secondary);
  flex-shrink: 0;
}

.is-list-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.is-list-count {
  font-size: 11px;
  color: var(--tc-text-tertiary);
}

/* ── Indicators List ── */
.is-indicators-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.is-indicators-list::-webkit-scrollbar {
  width: 6px;
}

.is-indicators-list::-webkit-scrollbar-track {
  background: transparent;
}

.is-indicators-list::-webkit-scrollbar-thumb {
  background: var(--tc-scrollbar-thumb);
  border-radius: 3px;
}

.is-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--tc-text-tertiary);
}

.is-empty-state svg {
  margin-bottom: 12px;
  opacity: 0.4;
}

.is-empty-state p {
  margin: 0 0 4px 0;
  font-weight: 500;
  color: var(--tc-text-secondary);
  font-size: 13px;
}

.is-empty-state span {
  font-size: 12px;
  text-align: center;
}

.is-indicator-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.1s ease;
  border-bottom: 1px solid var(--tc-border-secondary);
}

.is-indicator-item:hover {
  background: var(--tc-bg-hover);
}

.is-indicator-item.selected {
  background: var(--tc-accent-bg, rgba(59, 130, 246, 0.05));
}

.is-indicator-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.is-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid var(--tc-border-primary);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.is-checkbox.checked {
  background: var(--tc-accent);
  border-color: var(--tc-accent);
}

.is-indicator-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.is-indicator-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.is-indicator-desc {
  font-size: 11px;
  color: var(--tc-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.is-custom-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  background: var(--tc-accent-bg, rgba(59, 130, 246, 0.1));
  color: var(--tc-accent);
  border-radius: 10px;
  flex-shrink: 0;
}

/* ── Action Bar ── */
.is-action-bar {
  padding: 10px 16px 12px;
  border-top: 1px solid var(--tc-border-primary);
  background: var(--tc-bg-tertiary, var(--tc-bg-primary));
  flex-shrink: 0;
}

.is-selected-summary {
  margin-bottom: 8px;
}

.is-selected-count {
  font-size: 11px;
  font-weight: 600;
  color: var(--tc-text-secondary);
  margin-bottom: 4px;
  display: block;
}

.is-selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.is-selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: var(--tc-bg-primary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: var(--tc-text-primary);
}

.is-tag-more {
  background: var(--tc-bg-hover);
  color: var(--tc-text-secondary);
  border: none;
}

.is-tag-remove {
  background: none;
  border: none;
  color: var(--tc-text-tertiary);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
}

.is-tag-remove:hover {
  color: var(--tc-red);
}

.is-action-buttons {
  display: flex;
  gap: 8px;
}

.is-apply-btn {
  flex: 1;
  padding: 9px;
  background: var(--tc-accent);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.is-apply-btn:hover {
  background: var(--tc-accent-hover);
}

.is-group-btn {
  width: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tc-bg-tertiary, var(--tc-bg-hover));
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  color: var(--tc-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.is-group-btn:hover {
  background: var(--tc-accent-bg);
  border-color: var(--tc-accent);
  color: var(--tc-accent);
}
</style>

<style scoped>
.is-category-count{margin-left:auto;font-size:10px;opacity:.65;flex-shrink:0}.is-sidebar-item span:first-of-type{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
</style>
